import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import config from '../../../config';
import { resolveDashboardClientEnv } from '../../../utils/environmentMode';
import {
  DenyOrderArrivalOverrideRequest,
  DenyShipmentArrivalOverrideRequest,
  OverrideOrderArrival,
  OverrideShipmentArrival,
} from '../HelperFunctions/ShipmentFunctions';

const apiFilesHost = config.config.api_files.host;
const dashboardClientEnv = resolveDashboardClientEnv();

const ACTIVE_STATUSES = new Set(['pending', 'claimed_by_admin']);

function isOrderRequest(requestItem) {
  return requestItem && requestItem.waypointType === 'order' && requestItem.orderId;
}

const ArrivalOverrideRequestModal = ({
  isOpen,
  requestItem,
  notify,
  onClose,
  onRefresh,
}) => {
  const [adminReason, setAdminReason] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Stored signed_url on the request expires (7 days); re-sign per photo on open.
  const [freshPhotoUrls, setFreshPhotoUrls] = useState({});

  useEffect(() => {
    if (!isOpen || !requestItem || !requestItem.request) {
      setFreshPhotoUrls({});
      return undefined;
    }

    const photos = Array.isArray(requestItem.request.photos) ? requestItem.request.photos : [];
    const shipmentId = requestItem.shipmentId;
    if (photos.length === 0 || !shipmentId) {
      setFreshPhotoUrls({});
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const entries = await Promise.all(photos.map(async (photo, index) => {
        const fileName = photo && photo.file_name;
        const docType = (photo && photo.doc_type) || 'arrival_override_image';
        if (!fileName) {
          return [index, photo && photo.signed_url];
        }
        try {
          const response = await axios.get(
            `${apiFilesHost}/documents/signed-url?path_id=${encodeURIComponent(shipmentId)}&doc_type=${encodeURIComponent(docType)}&file_name=${encodeURIComponent(fileName)}&client_env=${encodeURIComponent(dashboardClientEnv)}`,
            { headers: { Authorization: 'Bearer ' + localStorage.getItem('access_token') } },
          );
          const freshUrl = response && response.data ? (response.data.url || response.data.signed_url) : null;
          return [index, freshUrl || (photo && photo.signed_url)];
        } catch (error) {
          return [index, photo && photo.signed_url];
        }
      }));

      if (!cancelled) {
        const nextUrls = {};
        entries.forEach(([index, url]) => { nextUrls[index] = url; });
        setFreshPhotoUrls(nextUrls);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, requestItem && requestItem.requestKey, requestItem && requestItem.shipmentId]);

  useEffect(() => {
    if (!isOpen) {
      setAdminReason('');
      setErrorMessage('');
      setIsSubmitting(false);
      return;
    }

    setAdminReason('');
    setErrorMessage('');
    setIsSubmitting(false);
  }, [isOpen, requestItem && requestItem.requestKey]);

  if (!requestItem || !requestItem.request) {
    return null;
  }

  const request = requestItem.request;
  const activeRequest = ACTIVE_STATUSES.has(request.status);
  const stopLabel = requestItem.stopType === 'pickup' ? 'Pickup stop' : 'Dropoff stop';
  const targetIdentifier = requestItem.orderId || requestItem.shipmentId;

  const handleApprove = async () => {
    const trimmedReason = adminReason.trim();
    if (!trimmedReason) {
      setErrorMessage('Dispatch notes are required before approving.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage('');

      const payload = {
        arrival_override: {
          reason: trimmedReason,
        },
        notes: trimmedReason,
      };

      if (isOrderRequest(requestItem)) {
        await OverrideOrderArrival(requestItem.shipmentId, requestItem.orderId, payload);
      } else {
        await OverrideShipmentArrival(requestItem.shipmentId, payload);
      }

      notify('tc', `${stopLabel} approved. Driver can continue the stop.`);
      if (onRefresh) {
        onRefresh();
      }
      onClose();
    } catch (error) {
      setErrorMessage(error.message || 'Unable to approve this arrival override request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeny = async () => {
    const trimmedReason = adminReason.trim();
    if (!trimmedReason) {
      setErrorMessage('Dispatch notes are required before denying.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage('');

      if (isOrderRequest(requestItem)) {
        await DenyOrderArrivalOverrideRequest(requestItem.shipmentId, requestItem.orderId, { notes: trimmedReason });
      } else {
        await DenyShipmentArrivalOverrideRequest(requestItem.shipmentId, { notes: trimmedReason });
      }

      notify('tc', `${stopLabel} override denied. Driver remains blocked until a new request is submitted.`, 'warning');
      if (onRefresh) {
        onRefresh();
      }
      onClose();
    } catch (error) {
      setErrorMessage(error.message || 'Unable to deny this arrival override request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} backdrop="static" size="lg">
      <ModalHeader toggle={onClose}>
        Arrival Override Review
      </ModalHeader>
      <ModalBody>
        <div style={{ color: '#000', display: 'grid', gap: '12px' }}>
          <div>
            <strong>{stopLabel}</strong>
            <div style={{ color: '#525f7f', marginTop: '4px' }}>
              Route {requestItem.routeFriendlyId || requestItem.routeId} | Target {targetIdentifier}
            </div>
            {requestItem.customerName ? (
              <div style={{ color: '#525f7f', marginTop: '4px' }}>Customer: {requestItem.customerName}</div>
            ) : null}
            {requestItem.driverName ? (
              <div style={{ color: '#525f7f', marginTop: '4px' }}>Driver: {requestItem.driverName}</div>
            ) : null}
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {request.distance_meters ? (
              <span style={{ background: '#fff4e8', borderRadius: '999px', color: '#8a5800', fontWeight: 700, padding: '6px 12px' }}>
                Distance {Math.round(request.distance_meters)}m
              </span>
            ) : null}
            {request.threshold_meters ? (
              <span style={{ background: '#fff4e8', borderRadius: '999px', color: '#8a5800', fontWeight: 700, padding: '6px 12px' }}>
                Radius {Math.round(request.threshold_meters)}m
              </span>
            ) : null}
            <span style={{ background: '#eef3ff', borderRadius: '999px', color: '#2b4fb5', fontWeight: 700, padding: '6px 12px' }}>
              Status {request.status.replace(/_/g, ' ')}
            </span>
          </div>

          <div style={{ background: '#f6f8fc', borderRadius: '10px', padding: '14px' }}>
            <div style={{ color: '#2b3553', fontWeight: 700, marginBottom: '6px' }}>Driver request</div>
            <div style={{ color: '#2b3553' }}>{request.reason || 'No driver reason supplied.'}</div>
            {request.notes ? <div style={{ color: '#525f7f', marginTop: '8px' }}>Notes: {request.notes}</div> : null}
            {Array.isArray(request.photos) && request.photos.length > 0 ? (
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '12px' }}>
                {request.photos.map((photo, index) => (
                  <a
                    key={`${photo.file_name || photo.signed_url || index}-${index}`}
                    href={freshPhotoUrls[index] || photo.signed_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <img
                      src={freshPhotoUrls[index] || photo.signed_url}
                      alt={`Arrival override evidence ${index + 1}`}
                      style={{ width: '88px', height: '88px', objectFit: 'cover', borderRadius: '12px', border: '1px solid #d9dee7' }}
                    />
                  </a>
                ))}
              </div>
            ) : null}
          </div>

          {activeRequest ? (
            <FormGroup>
              <Label for="arrival-override-review-notes">Dispatch review notes</Label>
              <Input
                id="arrival-override-review-notes"
                type="textarea"
                rows="4"
                value={adminReason}
                onChange={event => {
                  setAdminReason(event.target.value);
                  if (errorMessage) {
                    setErrorMessage('');
                  }
                }}
                placeholder="Required. Explain the dispatch decision for this arrival override request."
                style={{ color: '#212529', backgroundColor: '#ffffff', caretColor: '#212529' }}
              />
            </FormGroup>
          ) : null}

          {errorMessage ? <div style={{ color: '#dc3545' }}>{errorMessage}</div> : null}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={isSubmitting}>
          Close
        </Button>
        {activeRequest ? (
          <Button color="danger" onClick={handleDeny} disabled={isSubmitting || !adminReason.trim()}>
            Deny
          </Button>
        ) : null}
        {activeRequest ? (
          <Button color="success" onClick={handleApprove} disabled={isSubmitting || !adminReason.trim()}>
            Approve
          </Button>
        ) : null}
      </ModalFooter>
    </Modal>
  );
};

export default ArrivalOverrideRequestModal;